import {
    fromIntToMonetary,
    fromMonetaryToInt,
    subtractMonetaryValues,
    sumMonetaryValues,
} from "@common/helpers/monetary";
import {
    allocateProRataByOpenAmount,
    OpenWeightedLine,
} from "@common/helpers/pro-rata-allocation";
import { InvoiceLineItemKind } from "@common/types/invoice";

export interface InvoiceAllocationLine {
    id: string;
    amount: number;
    matchedAmount?: number;
    kind?: string | null;
    rowId?: string | null;
}

export type MatchedInvoiceAllocationLine = InvoiceAllocationLine & {
    matchedAmount: number;
};

export interface InvoiceLineAllocation {
    lineItemId: string;
    paymentAmount: number;
}

export function isStandaloneTaxAllocationLine(line: {
    kind?: string | null;
    rowId?: string | null;
}): boolean {
    return line.kind === InvoiceLineItemKind.TAX && line.rowId == null;
}

export function isStandaloneDiscountLine(line: {
    kind?: string | null;
    rowId?: string | null;
}): boolean {
    return line.kind === InvoiceLineItemKind.DISCOUNT && line.rowId == null;
}

export function isStandaloneDocumentAdjustmentLine(line: {
    kind?: string | null;
    rowId?: string | null;
}): boolean {
    return (
        line.rowId == null &&
        (line.kind === InvoiceLineItemKind.DISCOUNT ||
            line.kind === InvoiceLineItemKind.DEPOSIT)
    );
}

export function isInvoiceAllocationLine(line: {
    kind?: string | null;
    rowId?: string | null;
}): boolean {
    return (
        line.kind == null ||
        line.kind === InvoiceLineItemKind.ITEM ||
        isStandaloneTaxAllocationLine(line)
    );
}

function isItemAllocationLine(line: { kind?: string | null }): boolean {
    return line.kind == null || line.kind === InvoiceLineItemKind.ITEM;
}

export function invoiceLineContribution(line: {
    amount: number;
    kind?: string | null;
}): number {
    if (
        line.kind === InvoiceLineItemKind.DISCOUNT ||
        line.kind === InvoiceLineItemKind.DEPOSIT
    ) {
        return -line.amount;
    }
    return line.amount;
}

export function remainingInvoiceLineContribution(line: {
    amount: number;
    matchedAmount?: number;
    kind?: string | null;
}): number {
    const contribution = invoiceLineContribution(line);
    if (fromMonetaryToInt(contribution) === 0) {
        return 0;
    }
    const openMagnitudeCents = Math.max(
        0,
        fromMonetaryToInt(Math.abs(line.amount)) -
            fromMonetaryToInt(line.matchedAmount ?? 0),
    );
    return Math.sign(contribution) * fromIntToMonetary(openMagnitudeCents);
}

export function logicalInvoiceLineAmount(params: {
    itemId: string;
    itemAmount: number;
    lines: Array<{
        amount: number;
        kind?: string | null;
        rowId?: string | null;
    }>;
}): number {
    return sumMonetaryValues([
        params.itemAmount,
        ...params.lines
            .filter((line) => line.rowId === params.itemId)
            .map((line) => invoiceLineContribution(line)),
    ]);
}

export function logicalInvoiceLineMatchedAmount(params: {
    itemId: string;
    itemMatchedAmount: number;
    lines: Array<{
        matchedAmount: number;
        kind?: string | null;
        rowId?: string | null;
        amount: number;
    }>;
}): number {
    return sumMonetaryValues([
        params.itemMatchedAmount,
        ...params.lines
            .filter((line) => line.rowId === params.itemId)
            .map((line) => {
                const sign = Math.sign(invoiceLineContribution(line));
                return sign * line.matchedAmount;
            }),
    ]);
}

export function invoiceItemNetOpenAmounts(
    lines: MatchedInvoiceAllocationLine[],
): OpenWeightedLine[] {
    return lines.filter(isInvoiceAllocationLine).map((item) => ({
        id: item.id,
        openAmount: subtractMonetaryValues(
            logicalInvoiceLineAmount({
                itemId: item.id,
                itemAmount: item.amount,
                lines,
            }),
            logicalInvoiceLineMatchedAmount({
                itemId: item.id,
                itemMatchedAmount: item.matchedAmount,
                lines,
            }),
        ),
    }));
}

export function allocateInvoicePaymentToItems(params: {
    lines: MatchedInvoiceAllocationLine[];
    paymentAmount: number;
}): InvoiceLineAllocation[] {
    const itemAllocations = allocateProRataByOpenAmount(
        itemNetOpenAmounts(params.lines),
        params.paymentAmount,
    );
    const allocatedToItems = sumMonetaryValues(
        itemAllocations.map(([, paymentAmount]) => paymentAmount),
    );
    const remainingForTax = subtractMonetaryValues(
        params.paymentAmount,
        allocatedToItems,
    );
    const taxAllocations =
        fromMonetaryToInt(remainingForTax) > 0
            ? allocateProRataByOpenAmount(
                  standaloneTaxNetOpenAmounts(params.lines),
                  remainingForTax,
              )
            : [];

    return [...itemAllocations, ...taxAllocations]
        .map(([lineItemId, paymentAmount]) => ({ lineItemId, paymentAmount }))
        .filter((allocation) => allocation.paymentAmount > 0);
}

function itemNetOpenAmounts(
    lines: MatchedInvoiceAllocationLine[],
): OpenWeightedLine[] {
    return lines.filter(isItemAllocationLine).map((item) => ({
        id: item.id,
        openAmount: subtractMonetaryValues(
            logicalInvoiceLineAmount({
                itemId: item.id,
                itemAmount: item.amount,
                lines,
            }),
            logicalInvoiceLineMatchedAmount({
                itemId: item.id,
                itemMatchedAmount: item.matchedAmount,
                lines,
            }),
        ),
    }));
}

function standaloneTaxNetOpenAmounts(
    lines: MatchedInvoiceAllocationLine[],
): OpenWeightedLine[] {
    return lines.filter(isStandaloneTaxAllocationLine).map((line) => ({
        id: line.id,
        openAmount: subtractMonetaryValues(line.amount, line.matchedAmount),
    }));
}

export function expandInvoiceLineItemAllocations(params: {
    allocations: InvoiceLineAllocation[];
    lines: InvoiceAllocationLine[];
}): InvoiceLineAllocation[] {
    const linesById = new Map(params.lines.map((line) => [line.id, line]));
    const expanded: InvoiceLineAllocation[] = [];

    for (const allocation of params.allocations) {
        const line = linesById.get(allocation.lineItemId);
        if (line == null || !isInvoiceAllocationLine(line)) {
            expanded.push(allocation);
            continue;
        }

        const bundle = [
            line,
            ...params.lines.filter((candidate) => candidate.rowId === line.id),
        ];
        if (bundle.length === 1) {
            expanded.push(allocation);
            continue;
        }

        expanded.push(
            ...splitCollapsedInvoicePayment({
                paymentAmount: allocation.paymentAmount,
                bundle,
            }),
        );
    }

    return expanded;
}

/**
 * What a payment settles when nobody picked line amounts: the item rows by
 * their logical open amount, expanded across the tax and discount rows bundled
 * into each item. `null` when the invoice has no bundled rows or carries a
 * document-level discount or deposit, which keep their own open-amount cap.
 */
export function implicitInvoicePaymentAllocations(params: {
    lines: MatchedInvoiceAllocationLine[];
    paymentAmount: number;
}): InvoiceLineAllocation[] | null {
    const hasBundledRows = params.lines.some((line) => line.rowId != null);
    const hasDocumentAdjustment = params.lines.some(
        isStandaloneDocumentAdjustmentLine,
    );
    if (!hasBundledRows || hasDocumentAdjustment) {
        return null;
    }

    const openAmount = sumMonetaryValues(
        invoiceItemNetOpenAmounts(params.lines).map((item) =>
            Math.max(0, item.openAmount),
        ),
    );
    const paymentAmount = Math.min(params.paymentAmount, openAmount);
    if (fromMonetaryToInt(paymentAmount) <= 0) {
        return [];
    }

    return expandInvoiceLineItemAllocations({
        allocations: allocateInvoicePaymentToItems({
            lines: params.lines,
            paymentAmount,
        }),
        lines: params.lines,
    });
}

export function collapseInvoiceMatchesToItemAllocations(params: {
    matches: InvoiceLineAllocation[];
    lines: InvoiceAllocationLine[];
}): InvoiceLineAllocation[] {
    const linesById = new Map(params.lines.map((line) => [line.id, line]));
    const netAmountByItemId = new Map<string, number>();

    for (const match of params.matches) {
        const line = linesById.get(match.lineItemId);
        if (line == null) {
            continue;
        }
        const itemId = line.rowId ?? match.lineItemId;
        netAmountByItemId.set(
            itemId,
            sumMonetaryValues([
                netAmountByItemId.get(itemId) ?? 0,
                Math.sign(invoiceLineContribution(line)) * match.paymentAmount,
            ]),
        );
    }

    return [...netAmountByItemId.entries()]
        .map(([lineItemId, paymentAmount]) => ({ lineItemId, paymentAmount }))
        .filter(
            (allocation) => fromMonetaryToInt(allocation.paymentAmount) > 0,
        );
}

export function splitCollapsedInvoicePayment(params: {
    paymentAmount: number;
    bundle: InvoiceAllocationLine[];
}): InvoiceLineAllocation[] {
    const weights = params.bundle.map((line) => ({
        id: line.id,
        weight: remainingInvoiceLineContribution(line),
    }));
    const remainingCollapsed = sumMonetaryValues(
        weights.map((weight) => weight.weight),
    );
    const remainingCents = fromMonetaryToInt(remainingCollapsed);
    const paymentCents = fromMonetaryToInt(params.paymentAmount);

    if (paymentCents === 0) {
        return [];
    }
    // A non-zero payment against a fully-matched bundle (no open amount left)
    // is an over-allocation and must be rejected, mirroring the partially-open
    // path below, rather than silently dropped.
    if (remainingCents === 0) {
        throw new InvoiceLineAllocationExceedsOpenError();
    }
    if (Math.sign(paymentCents) !== Math.sign(remainingCents)) {
        throw new InvoiceLineAllocationExceedsOpenError();
    }
    if (Math.abs(paymentCents) > Math.abs(remainingCents)) {
        throw new InvoiceLineAllocationExceedsOpenError();
    }

    return splitAmountBySignedWeights({
        amount: params.paymentAmount,
        parts: weights,
    }).map((share) => ({
        lineItemId: share.id,
        paymentAmount: share.amount,
    }));
}

export function allocateSignedPaymentToLines(params: {
    lines: InvoiceAllocationLine[];
    paymentAmount: number;
}): InvoiceLineAllocation[] {
    const weights = params.lines.map((line) => ({
        id: line.id,
        weight: remainingInvoiceLineContribution(line),
    }));
    const remainingAmount = sumMonetaryValues(
        weights.map((weight) => weight.weight),
    );
    if (fromMonetaryToInt(params.paymentAmount) === 0) {
        return [];
    }
    if (
        fromMonetaryToInt(remainingAmount) === 0 ||
        Math.sign(params.paymentAmount) !== Math.sign(remainingAmount) ||
        Math.abs(fromMonetaryToInt(params.paymentAmount)) >
            Math.abs(fromMonetaryToInt(remainingAmount))
    ) {
        throw new InvoiceLineAllocationExceedsOpenError();
    }
    return splitAmountBySignedWeightsWithinOpenAmounts({
        amount: params.paymentAmount,
        parts: weights,
    }).map((share) => ({
        lineItemId: share.id,
        paymentAmount: share.amount,
    }));
}

export class InvoiceLineAllocationExceedsOpenError extends Error {
    constructor() {
        super("Allocation exceeds line item open amount");
        this.name = "InvoiceLineAllocationExceedsOpenError";
    }
}

function splitAmountBySignedWeights(params: {
    amount: number;
    parts: Array<{ id: string; weight: number }>;
}): Array<{ id: string; amount: number }> {
    const amountCents = fromMonetaryToInt(params.amount);
    const parts = params.parts.filter(
        (part) => fromMonetaryToInt(part.weight) !== 0,
    );
    if (parts.length === 0 || amountCents === 0) {
        return [];
    }

    const weightCents = parts.map((part) => fromMonetaryToInt(part.weight));
    const totalWeightCents = weightCents.reduce(
        (sum, weight) => sum + weight,
        0,
    );
    if (totalWeightCents === 0) {
        return [];
    }

    let allocatedCents = 0;
    const shares = parts.map((part, index) => {
        const cents = Math.trunc(
            (amountCents * weightCents[index]) / totalWeightCents,
        );
        allocatedCents += cents;
        return { id: part.id, cents };
    });

    const remainderCents = amountCents - allocatedCents;
    if (remainderCents !== 0) {
        let largestIndex = 0;
        for (let index = 1; index < parts.length; index++) {
            if (
                Math.abs(weightCents[index]) >=
                Math.abs(weightCents[largestIndex])
            ) {
                largestIndex = index;
            }
        }
        shares[largestIndex].cents += remainderCents;
    }

    return shares
        .filter((share) => share.cents !== 0)
        .map((share) => ({
            id: share.id,
            amount: fromIntToMonetary(share.cents),
        }));
}

function splitAmountBySignedWeightsWithinOpenAmounts(params: {
    amount: number;
    parts: Array<{ id: string; weight: number }>;
}): Array<{ id: string; amount: number }> {
    const amountCents = fromMonetaryToInt(params.amount);
    const parts = params.parts.filter(
        (part) => fromMonetaryToInt(part.weight) !== 0,
    );
    if (parts.length === 0 || amountCents === 0) {
        return [];
    }

    const weightCents = parts.map((part) => fromMonetaryToInt(part.weight));
    const totalWeightCents = weightCents.reduce(
        (sum, weight) => sum + weight,
        0,
    );
    if (totalWeightCents === 0) {
        return [];
    }

    let allocatedCents = 0;
    const shares = parts.map((part, index) => {
        const scaledCents = amountCents * weightCents[index];
        const cents = Math.trunc(scaledCents / totalWeightCents);
        allocatedCents += cents;
        return {
            id: part.id,
            cents,
            index,
            weight: weightCents[index],
            remainder:
                (scaledCents - cents * totalWeightCents) *
                Math.sign(totalWeightCents),
        };
    });

    const remainderCents = amountCents - allocatedCents;
    if (remainderCents !== 0) {
        const remainderSign = Math.sign(remainderCents);
        const candidates = shares
            .filter((share) => Math.sign(share.remainder) === remainderSign)
            .sort(
                (left, right) =>
                    Math.abs(right.weight) - Math.abs(left.weight) ||
                    right.index - left.index,
            );
        for (let index = 0; index < Math.abs(remainderCents); index++) {
            const candidate = candidates.find(
                (share) =>
                    Math.abs(share.cents + remainderSign) <=
                    Math.abs(share.weight),
            );
            if (candidate === undefined) {
                throw new InvoiceLineAllocationExceedsOpenError();
            }
            candidate.cents += remainderSign;
            candidates.splice(candidates.indexOf(candidate), 1);
        }
    }

    return shares
        .filter((share) => share.cents !== 0)
        .map((share) => ({
            id: share.id,
            amount: fromIntToMonetary(share.cents),
        }));
}
